import { queryOptions } from '@tanstack/react-query'
import { api } from './client'
import type {
  BusinessOpportunity,
  Division,
  PaginatedResponse,
  ProductTeam,
  StrategicIntent,
  UnitSummary,
  ValueStream,
} from '../types'

const ORGANIZATION_BROWSE_PAGE_SIZE = 500

export const organizationBrowseKeys = {
  units: (organizationId: string, levelCode?: string, includeArchived?: boolean, locale?: string) =>
    [
      'units', 'byOrganization', organizationId, levelCode ?? 'all',
      ...(includeArchived ? ['withArchived'] : []),
      ...(locale ? [locale] : []),
    ] as const,
  divisions: (organizationId: string) => ['divisions', 'byOrganization', organizationId] as const,
  valueStreams: (organizationId: string) => ['valueStreams', 'byOrganization', organizationId] as const,
  productTeams: (organizationId: string) => ['productTeams', 'byOrganization', organizationId] as const,
  strategicIntents: (organizationId: string) => ['strategicIntents', 'byOrganization', organizationId] as const,
  businessOpportunities: (organizationId: string) =>
    ['businessOpportunities', 'byOrganization', organizationId] as const,
}

export const organizationBrowseQueries = {
  /**
   * Units at one level of this organization's ladder, or all of them.
   *
   * Replaces `divisions`, `valueStreams` and `productTeams` for anything that
   * does not need the level-specific fields those carry. Those three stay for
   * now -- forms still edit a division as a division -- but a screen that only
   * lists or navigates should ask for units, because that keeps working for a
   * tenant whose ladder has a level the other three have no name for.
   */
  units: (organizationId: string, levelCode?: string, includeArchived?: boolean, locale?: string) => queryOptions({
    queryKey: organizationBrowseKeys.units(organizationId, levelCode, includeArchived, locale),
    queryFn: () =>
      api.operatingModel
        .units(
          organizationId,
          levelCode || includeArchived || locale
            ? {
                ...(levelCode ? { levelCode } : {}),
                // Deactivated units too, where a screen offers to reactivate them.
                ...(includeArchived ? { includeArchived: true } : {}),
                // `levelName` in this language; without it the server answers in English.
                ...(locale ? { locale } : {}),
              }
            : undefined,
        )
        .then((response) => response.data as UnitSummary[]),
  }),
  divisions: (organizationId: string) => queryOptions({
    queryKey: organizationBrowseKeys.divisions(organizationId),
    queryFn: () =>
      api.divisions
        .list(organizationId, { size: ORGANIZATION_BROWSE_PAGE_SIZE })
        .then((response) => response.data as PaginatedResponse<Division>),
  }),
  valueStreams: (organizationId: string) => queryOptions({
    queryKey: organizationBrowseKeys.valueStreams(organizationId),
    queryFn: () =>
      api.valueStreams
        .listByOrganization(organizationId, { size: ORGANIZATION_BROWSE_PAGE_SIZE })
        .then((response) => response.data as PaginatedResponse<ValueStream>),
  }),
  productTeams: (organizationId: string) => queryOptions({
    queryKey: organizationBrowseKeys.productTeams(organizationId),
    queryFn: () =>
      api.productTeams
        .list(organizationId, { size: ORGANIZATION_BROWSE_PAGE_SIZE })
        .then((response) => response.data as PaginatedResponse<ProductTeam>),
  }),
  strategicIntents: (organizationId: string) => queryOptions({
    queryKey: organizationBrowseKeys.strategicIntents(organizationId),
    queryFn: () =>
      api.strategicIntents
        .listByOrganization(organizationId, { size: ORGANIZATION_BROWSE_PAGE_SIZE })
        .then((response) => response.data as PaginatedResponse<StrategicIntent>),
  }),
  businessOpportunities: (organizationId: string) => queryOptions({
    queryKey: organizationBrowseKeys.businessOpportunities(organizationId),
    queryFn: () =>
      api.businessOpportunities
        .listByOrganization(organizationId, { size: ORGANIZATION_BROWSE_PAGE_SIZE })
        .then((response) => response.data as PaginatedResponse<BusinessOpportunity>),
  }),
}
